import { LoadingButton } from '@mui/lab';
import { Breakpoint, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { MouseEvent, ReactNode } from 'react';
import React from 'react';
import { BUTTON_VARIANTS } from '../constants/constants';
import { useI18n } from '../i18n/I18n';

type ConfirmProps = {
    open: boolean;
    title?: ReactNode;
    body: ReactNode;
    okLabelI18n?: string;
    cancelLabelI18n?: string;
    onConfirm: (event: MouseEvent<HTMLButtonElement>) => void;
    onCancel: (event: MouseEvent<HTMLButtonElement>) => void;
    maxWidth?: Breakpoint;
    isLoading?: boolean;
    maxHeight?: number;
    isOkButtonContained?: boolean;
    isCancelButtonOutlined?: boolean;
    buttonWrapperSx?: SxProps<Theme>;
    isConfirmButtonDisabled?: boolean;
};

export default function Confirm({
    open,
    title,
    body,
    okLabelI18n = 'dialog.ok',
    cancelLabelI18n = 'dialog.cancel',
    onConfirm,
    onCancel,
    maxWidth = 'xs',
    isLoading = false,
    maxHeight = 435,
    isOkButtonContained = false,
    isCancelButtonOutlined = false,
    buttonWrapperSx = {},
    isConfirmButtonDisabled = false
}: ConfirmProps) {
    const okLabel = useI18n(okLabelI18n);
    const cancelLabel = useI18n(cancelLabelI18n);
    return (
        <Dialog sx={{ '& .MuiDialog-paper': { width: '80%', maxHeight: maxHeight } }} maxWidth={maxWidth} open={open}>
            {title && <DialogTitle>{title}</DialogTitle>}
            <DialogContent dividers>{body}</DialogContent>
            <DialogActions sx={buttonWrapperSx}>
                <Button
                    autoFocus
                    onClick={(event: MouseEvent<HTMLButtonElement>) => onCancel(event)}
                    variant={isCancelButtonOutlined ? BUTTON_VARIANTS.outlined : BUTTON_VARIANTS.text}
                >
                    {cancelLabel}
                </Button>
                <LoadingButton
                    onClick={(event: MouseEvent<HTMLButtonElement>) => onConfirm(event)}
                    loading={isLoading}
                    variant={isOkButtonContained ? BUTTON_VARIANTS.contained : BUTTON_VARIANTS.text}
                    disabled={isConfirmButtonDisabled}
                >
                    {okLabel}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
